// Devolucao de Pix pela Conciliacao Bancaria (DA-506) — o pedir-valor e o desfecho.
//
// Duas telas, na ordem em que o operador as ve:
//  1. `ModalEstornoPix`  — quanto devolver. O valor NUNCA vem preenchido (regra do Ruan): o saldo
//     livre aparece como texto e como atalho clicavel, mas quem digita e quem esta com o cliente na
//     frente. Um campo pre-preenchido com o total convida o Enter distraido a devolver o Pix inteiro.
//  2. `ComprovanteDevolucaoModal` — o desfecho. Vai em MODAL, nunca em toast (DA-443): o protocolo
//     da devolucao no Mercado Pago so existe aqui, e e o unico numero que o cliente leva ao banco
//     quando o dinheiro demora. Toast some sozinho e leva o protocolo junto.
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Undo2, Loader2, Smartphone, Printer, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api, getToken } from '../../lib/api';
import { useToast } from '../Toast';
import { useAviso } from '../Aviso';
import { useDialog } from '../DialogProvider';
import { ModalEnviarWhatsAppCliente } from '../whatsapp/ModalEnviarWhatsAppCliente';
import { formatMoeda } from '@fiodecobre/shared';

// pt-BR: o operador digita "149,99". `input type=number` esta proibido pra reais (CLAUDE.md).
function parseMoney(val: string | null | undefined): number {
  if (!val) return 0;
  return parseFloat(String(val).replace(/\./g, '').replace(',', '.')) || 0;
}
function fmtMoney(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export interface EstornoRealizado {
  devolucao_id: number | null;
  refund_id: string;
  valor: number;
  saldo_restante: number;
  cliente_id: number | null;
  cliente_nome: string | null;
}

interface PropsEstorno {
  recebimentoId: number;
  valorBruto: number;
  saldoLivre: number;
  pagadorNome: string | null;
  clienteNome: string | null;
  onFeito: (r: EstornoRealizado) => void;
  onCancelar: () => void;
}

export function ModalEstornoPix({ recebimentoId, valorBruto, saldoLivre, pagadorNome, clienteNome, onFeito, onCancelar }: PropsEstorno) {
  const aviso = useAviso();
  const dialog = useDialog();
  const [valorTexto, setValorTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const valor = parseMoney(valorTexto);
  // Regua de centavo do sistema (DA-433): 72,01 num saldo de 72,00 nao e erro de digitacao.
  const passaDoSaldo = valor > saldoLivre + 0.05;
  const podeEnviar = valor > 0 && !passaDoSaldo && !enviando;
  // O que sobra pra loja. Trava no zero: com a tolerancia de centavo, pedir 72,01 num saldo de
  // 72,00 daria -0,01 e a tela mostraria um negativo que nao existe.
  const restante = Math.max(0, Number((saldoLivre - valor).toFixed(2)));

  async function confirmar() {
    if (!podeEnviar) return;
    const ok = await dialog.confirmar(
      `Devolver ${formatMoeda(valor)} ao cliente${clienteNome ? ` (${clienteNome})` : ''}?\n\n`
      + `O dinheiro sai da conta do Mercado Pago e volta pra mesma conta que fez o Pix. `
      + `Essa devolução é IRREVERSÍVEL — não dá pra recobrar depois.\n\n`
      + (restante > 0.005
          ? `Fica com a loja ${formatMoeda(restante)} deste Pix.`
          : `Isso devolve o saldo inteiro deste Pix.`),
      { titulo: 'Devolver este Pix?', textoConfirmar: 'Sim, quero devolver', perigoso: true },
    );
    if (!ok) return;
    setEnviando(true);
    try {
      const r = await api.post<EstornoRealizado>(`/api/mercadopago/recebimentos/${recebimentoId}/estornar`, { valor });
      onFeito(r);
    } catch (e: any) {
      // Recusa vem em modal, nao em toast: e dinheiro, e a mensagem explica o que fazer antes.
      aviso.erro(e.message);
    } finally {
      setEnviando(false);
    }
  }

  // z-[70] e NAO z-[1100]: o `dialog.confirmar` da casa e desenhado com o `Modal`, cujo overlay e
  // `z-index: 1000` (styles.css). Com 1100 aqui, a confirmacao de "quer mesmo devolver?" abria ATRAS
  // deste modal e ficava meio escondida — exatamente o passo que precisa ser lido. 1100 e o andar de
  // quem abre DE DENTRO de um modal (ModalSenhaGerencial, seletor de WhatsApp); este abre da pagina.
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
      <div className="rounded-xl shadow-2xl p-6 w-full max-w-md mx-4" style={{ backgroundColor: 'var(--panel-bg)' }}>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#fde68a' }}>
            <Undo2 size={20} style={{ color: '#000' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg">Devolver Pix</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {clienteNome ?? pagadorNome ?? 'Pagador não identificado'}
            </div>
          </div>
          <button type="button" onClick={onCancelar} className="p-1 rounded-lg" aria-label="Fechar">
            <X size={20} style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: 'var(--page-bg)' }}>
          <div className="flex justify-between"><span style={{ color: 'var(--text-muted)' }}>Pix recebido</span><span className="font-semibold">{formatMoeda(valorBruto)}</span></div>
          <div className="flex justify-between mt-1">
            <span style={{ color: 'var(--text-muted)' }}>Disponível para devolver</span>
            <button type="button" onClick={() => setValorTexto(fmtMoney(saldoLivre))}
              className="font-bold underline" style={{ color: 'var(--primary)' }}
              title="Usar o saldo inteiro">
              {formatMoeda(saldoLivre)}
            </button>
          </div>
        </div>

        <label className="block mt-4 text-sm font-semibold">Valor a devolver</label>
        <input
          type="text"
          inputMode="decimal"
          autoFocus
          value={valorTexto}
          onChange={(e) => setValorTexto(e.target.value)}
          onBlur={() => { if (valorTexto) setValorTexto(fmtMoney(parseMoney(valorTexto))); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && podeEnviar) void confirmar(); }}
          placeholder="0,00"
          className="mt-1 w-full px-3 py-2.5 rounded-lg border text-right font-bold"
          style={{ fontSize: 20, backgroundColor: 'var(--page-bg)', borderColor: passaDoSaldo ? 'var(--danger,#dc2626)' : 'var(--border,#d1d5db)' }}
        />

        {passaDoSaldo && (
          <div className="mt-2 text-xs font-semibold flex items-center gap-1.5" style={{ color: 'var(--danger,#dc2626)' }}>
            <AlertTriangle size={13} /> Passa do saldo deste Pix ({formatMoeda(saldoLivre)}).
          </div>
        )}
        {/* Previsao do que SOBRA — em destaque, nao em letra miuda (pedido do Ruan, 11/08). Este e o
            numero que o operador precisa reconhecer antes de confirmar: e ele que tem que bater com o
            pedido que o cliente esta levando. Amarelo+preto e o par que o Ruan enxerga (daltonismo
            verde/vermelho), o mesmo dos selos positivos da casa. Aparece tambem quando zera, senao
            o bloco sumiria justo no caso "devolvi tudo" e a ausencia pareceria erro de tela. */}
        {valor > 0 && !passaDoSaldo && (
          <div className="mt-3 rounded-lg px-4 py-3 flex items-center justify-between gap-3"
            style={{ backgroundColor: '#fde68a', color: '#000' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6 }}>FICA COM A LOJA</div>
              <div style={{ fontSize: 11 }}>
                {restante > 0.005 ? 'depois desta devolução' : 'este Pix fica zerado'}
              </div>
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>
              {formatMoeda(restante)}
            </div>
          </div>
        )}

        <div className="mt-3 text-xs rounded-lg p-2.5" style={{ backgroundColor: '#fef3c7', color: '#000' }}>
          A devolução é feita no Mercado Pago e não tem volta. Confira o valor antes de confirmar.
        </div>

        <button type="button" onClick={() => void confirmar()} disabled={!podeEnviar}
          className="mt-4 w-full px-4 py-2.5 rounded-lg text-white flex items-center justify-center gap-2 disabled:opacity-40"
          style={{ backgroundColor: '#dc2626' }}>
          {enviando ? <Loader2 size={16} className="animate-spin" /> : <Undo2 size={16} />}
          Devolver {valor > 0 ? formatMoeda(valor) : ''}
        </button>
        <button type="button" onClick={onCancelar} disabled={enviando}
          className="mt-2 w-full px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--page-bg)' }}>
          Cancelar
        </button>
      </div>
    </div>,
    document.body,
  );
}

// --- Desfecho: comprovante + envio ---

interface PropsComprovante {
  estorno: EstornoRealizado;
  onFechar: () => void;
}

export function ComprovanteDevolucaoModal({ estorno, onFechar }: PropsComprovante) {
  const toast = useToast();
  const aviso = useAviso();
  const [enviarAberto, setEnviarAberto] = useState(false);
  const [enviadoPara, setEnviadoPara] = useState<string | null>(null);
  const devId = estorno.devolucao_id;

  // Imprime o JPG numa janela de 55mm. O `onload` antes do print evita a folha em branco quando a
  // imagem ainda nao decodificou.
  //
  // ⚠️ A imagem tem que vir por FETCH AUTENTICADO, nunca por `<img src="/api/...">`: a rota do
  // comprovante esta atras do `autenticar` e o token viaja no header `Authorization`, que uma tag
  // <img> numa janela nova NAO manda. O pedido voltava 401 e a janela abria com o icone de imagem
  // quebrada — sem erro em lugar nenhum, porque do ponto de vista do JS nada falhou. Mesma receita
  // do comprovante do Pix em `CobrancaPix.tsx`.
  const [imprimindo, setImprimindo] = useState(false);
  async function imprimir() {
    if (!devId) return;
    // Janela aberta ANTES do await: depois dele o navegador trata como popup e bloqueia.
    const w = window.open('', '_blank');
    if (!w) { aviso.erro('O navegador bloqueou a janela de impressão. Libere os pop-ups deste site.'); return; }
    setImprimindo(true);
    try {
      const token = getToken();
      const resp = await fetch(`/api/mercadopago/devolucoes/${devId}/comprovante`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const url = URL.createObjectURL(await resp.blob());
      w.document.write(
        `<html><head><title>Comprovante de devolucao</title><style>@page{size:55mm auto;margin:0}`
        + `body{margin:0}img{width:100%;display:block}</style></head>`
        + `<body><img src="${url}" onload="window.print();window.close()"></body></html>`,
      );
      w.document.close();
      // Nao revogar na mesma pilha — cancelaria o carregamento antes de o navegador comecar.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      w.close();
      aviso.erro(`Não foi possível gerar o comprovante da devolução (${(e as Error).message}). O protocolo ${estorno.refund_id} continua valendo no banco.`);
    } finally {
      setImprimindo(false);
    }
  }

  async function enviarPara(digits: string) {
    if (!devId) throw new Error('comprovante indisponível');
    await api.post(`/api/mercadopago/devolucoes/${devId}/enviar-whatsapp`, { jid: digits });
    setEnviadoPara(digits);
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
        <div className="rounded-xl shadow-2xl p-6 w-full max-w-md mx-4" style={{ backgroundColor: 'var(--panel-bg)' }}>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#fde68a' }}>
              <CheckCircle2 size={22} style={{ color: '#000' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-lg">Devolução concluída</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>O dinheiro voltou pra conta que fez o Pix</div>
              {estorno.cliente_nome && <div className="text-sm font-medium truncate">{estorno.cliente_nome}</div>}
            </div>
          </div>

          <div className="rounded-lg p-3 text-sm space-y-1" style={{ backgroundColor: 'var(--page-bg)' }}>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-muted)' }}>Devolvido</span>
              <span style={{ fontSize: 16, fontWeight: 700 }}>{formatMoeda(estorno.valor)}</span>
            </div>
            {estorno.saldo_restante > 0.005 && (
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-muted)' }}>Permanece na compra</span>
                <span className="font-semibold">{formatMoeda(estorno.saldo_restante)}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-1">
              <span style={{ color: 'var(--text-muted)' }}>Protocolo</span>
              <span className="font-mono font-bold" style={{ fontSize: 13 }}>{estorno.refund_id}</span>
            </div>
          </div>

          <div className="mt-3 text-xs rounded-lg p-2.5" style={{ backgroundColor: '#fef3c7', color: '#000' }}>
            O protocolo acima é o que o cliente leva ao banco se o dinheiro demorar. Se fechar esta
            tela, dá pra voltar aqui pelo botão <strong>Comprovante</strong> na linha do estorno.
          </div>

          <button type="button" onClick={imprimir} disabled={!devId || imprimindo}
            className="mt-3 w-full px-4 py-2.5 rounded-lg text-white flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ backgroundColor: 'var(--primary,#2563eb)' }}>
            {imprimindo ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
            {imprimindo ? 'Gerando...' : 'Imprimir comprovante'}
          </button>

          {/* Sem trava por cliente identificado: o envio ABRE a lista de destinatarios e quem escolhe
              e quem esta com o cliente na frente (DA-347). Com cliente vinculado a lista ja vem com os
              contatos dele marcados; sem cliente, abre na busca por nome. Travar aqui deixava o
              documento que MAIS precisa chegar ao cliente sem caminho nenhum — e a nota antiga mandava
              usar o "Identificar", que em banco sem numero de conta (Caixa, p.ex.) nem aparece na tela. */}
          <button type="button" onClick={() => setEnviarAberto(true)} disabled={!devId}
            className="mt-2 w-full px-4 py-2.5 rounded-lg text-white flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ backgroundColor: '#16a34a' }}>
            <Smartphone size={16} /> {enviadoPara ? 'Enviar novamente' : 'Enviar comprovante no WhatsApp'}
          </button>

          {enviadoPara && (
            <div className="mt-3 w-full px-4 py-2.5 rounded-lg text-center text-sm" style={{ backgroundColor: '#fde68a', color: '#000' }}>
              Comprovante enviado para <span className="font-bold">{enviadoPara}</span>
            </div>
          )}

          <button type="button" onClick={onFechar}
            className="mt-4 w-full px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2" style={{ backgroundColor: 'var(--page-bg)' }}>
            <X size={14} /> Fechar
          </button>
        </div>
      </div>

      {enviarAberto && (
        <ModalEnviarWhatsAppCliente
          clienteId={estorno.cliente_id ?? null}
          titulo="Enviar comprovante de devolução"
          contextoNome={estorno.cliente_nome ?? undefined}
          contextoExtra={`Devolução de ${formatMoeda(estorno.valor)} — protocolo ${estorno.refund_id}`}
          arquivoLabel="Comprovante de devolução (imagem)"
          enviarPara={enviarPara}
          onClose={(enviouAlgum) => {
            setEnviarAberto(false);
            if (enviouAlgum) toast.sucesso('Comprovante enviado');
          }}
        />
      )}
    </>,
    document.body,
  );
}
